'use client'
import * as React from 'react'
import {
  Branch,
  ContentRequest,
  isTypeForBlockWithChildren,
} from '@basehub/data/replicache/index'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  UpdateIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { parseTab, useTabsStore } from '../../explore/components/tabs/store'
import { useBranchMergeDiff } from '../../branch/_hooks/use-merge-diff'
import { useMergeMutation } from '../_components/branch-actions-cta'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { SetRequired } from 'type-fest'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { isUserRepo } from '@basehub/data/replicache/mutators/_public-types'

type Conflict = {
  blockId: string
  edgeId: string | null
  resolved: boolean
}

export const calculateConflicts = ({
  conflictResolutions,
  changesTree,
}: {
  conflictResolutions: ContentRequest['conflictResolutions']
  changesTree?: SetRequired<
    ReturnType<typeof useBranchMergeDiff>['diffResult'],
    'diff'
  >['diff']['changesTree']
}) => {
  let resolvedCount = 0
  let unresolvedCount = 0
  const conflictsWizardIndex =
    changesTree?.compoundIndexes.get('conflictsWizard') ?? new Set<string>()
  const conflicts: Conflict[] = []
  conflictsWizardIndex.forEach((nodeKey) => {
    const node = changesTree?.getNode(nodeKey)
    if (!node) return
    if (node.value.status !== 'conflict') return

    const isConflictOnDeletion =
      node.value.conflictType === 'deleted-in-source' ||
      node.value.conflictType === 'deleted-in-head'

    const resolution = isConflictOnDeletion
      ? !!conflictResolutions?.[nodeKey]
      : node.value.conflictPaths.every((path) => {
          if (node.value.type === 'rich-text' && path.includes('value')) {
            const index = path.split('.')[2]
            return conflictResolutions?.[`${nodeKey}/${index}`]
          } else {
            return !!conflictResolutions?.[nodeKey]
          }
        })
    const isEdgeBlock =
      !isTypeForBlockWithChildren(node.value.type) ||
      ['rich-text', 'union'].includes(node.value.type)

    conflicts.push({
      blockId: isEdgeBlock ? (node.parentKey ?? node.key) : node.key,
      edgeId: isEdgeBlock ? node.key : null,
      resolved: !!resolution,
    })

    if (resolution) {
      resolvedCount++
    } else {
      unresolvedCount++
    }
  })

  return { conflicts, resolvedCount, unresolvedCount }
}

export const useConflictsWizard = ({ branch }: { branch: Branch }) => {
  const { diffResult, cr } = useBranchMergeDiff({ branch })
  const activeBlockId = useTabsStore((s) => {
    const parsed = s.activeTab ? parseTab(s.activeTab) : s.activeTab
    if (parsed?.type !== 'merge') return null
    return parsed.edgeId ?? parsed.blockId
  })
  const addTab = useTabsStore((s) => s.addTab)

  const { conflicts, resolvedCount, unresolvedCount } = React.useMemo(
    () =>
      calculateConflicts({
        changesTree: diffResult.diff?.changesTree,
        conflictResolutions: cr?.conflictResolutions,
      }),
    [cr?.conflictResolutions, diffResult.diff?.changesTree]
  )

  const totalCount = conflicts.length
  const activeConflictIndex = conflicts.findIndex((c) =>
    c.edgeId ? c.edgeId === activeBlockId : c.blockId === activeBlockId
  )

  const goToConflict = React.useCallback(
    (c: Conflict) => {
      if (!cr?.id) return
      addTab({
        key: `merge:${cr.id}:${c.blockId}${c.edgeId ? `:${c.edgeId}` : ''}`,
        partiallyOpen: true,
      })
    },
    [addTab, cr?.id]
  )

  const prev = React.useMemo(() => {
    if (activeConflictIndex === -1) {
      // select the first non-resolved conflict
      const prevConflict = conflicts.find((c) => !c.resolved)
      return {
        canPrev: !!prevConflict,
        goPrev: () => (prevConflict ? goToConflict(prevConflict) : undefined),
      }
    } else {
      const prevConflict = conflicts[activeConflictIndex - 1]
      return {
        canPrev: !!prevConflict,
        goPrev: () => (prevConflict ? goToConflict(prevConflict) : undefined),
      }
    }
  }, [activeConflictIndex, conflicts, goToConflict])

  const next = React.useMemo(() => {
    if (activeConflictIndex === -1) {
      // select the first non-resolved conflict
      const nextConflict = conflicts.find((c) => !c.resolved)
      return {
        canNext: !!nextConflict,
        goNext: () => (nextConflict ? goToConflict(nextConflict) : undefined),
      }
    } else {
      const nextConflict = conflicts[activeConflictIndex + 1]
      return {
        canNext: !!nextConflict,
        goNext: () => (nextConflict ? goToConflict(nextConflict) : undefined),
      }
    }
  }, [activeConflictIndex, conflicts, goToConflict])

  return {
    cr,
    totalCount,
    prev,
    next,
    resolvedCount,
    unresolvedCount,
    allResolved: resolvedCount === totalCount,
    conflicts,
    activeConflictIndex,
    activeConflict: conflicts[activeConflictIndex],
    goToConflict,
  }
}

export const ConflictsWizard = ({
  className,
  branch,
}: {
  className?: string
  branch: Branch
}) => {
  const {
    activeConflictIndex,
    unresolvedCount,
    conflicts,
    totalCount,
    allResolved,
    cr,
    prev,
    next,
    goToConflict,
  } = useConflictsWizard({ branch })
  const mergeMutation = useMergeMutation()
  const repo = useSelectedTeamAndRepo()?.data?.repo

  if (!totalCount) return null
  return (
    <div
      className={clsx(
        'flex justify-center items-center flex-col relative z-50 border border-dark-border-solid bg-dark-base shadow-small-card rounded-lg overflow-hidden min-w-60',
        className
      )}
    >
      <button
        onClick={() => {
          if (!cr) return
          if (mergeMutation.isLoading) return
          if (allResolved) {
            if (isUserRepo(repo)) {
              mergeMutation.mutate({
                cr,
                type: 'update',
                archive: repo.branching?.archiveAfterMerging ?? false,
              })
            }
          }
        }}
        disabled={!allResolved}
        className="enabled:bg-green enabled:hover:bg-green/80 enabled:text-dark-bright flex items-center gap-1.5 h-8 text-sm font-medium px-3 cursor-default w-full justify-center"
      >
        {allResolved ? (
          <>
            {mergeMutation.isLoading
              ? 'Updating...'
              : 'Resolve and update branch'}
            <UpdateIcon />
          </>
        ) : (
          <span>
            {unresolvedCount} {unresolvedCount === 1 ? 'conflict' : 'conflicts'}{' '}
            to resolve
          </span>
        )}
      </button>
      <ScrollArea orientation="horizontal" variant="subtle">
        <div className="text-xs text-dark-muted flex items-center gap-1.5 h-8 px-3 max-w-96">
          <IconButton
            onClick={prev.goPrev}
            disabled={!prev.canPrev}
            aria-label="Previous"
          >
            <ArrowLeftIcon />
          </IconButton>
          {conflicts.map((c, i) => {
            const isActive = i === activeConflictIndex
            return (
              <button
                key={c.blockId}
                className={clsx(
                  'h-6 w-6 flex items-center justify-center rounded-full overflow-hidden cursor-default shrink-0',
                  c.resolved
                    ? isActive
                      ? 'bg-green text-white'
                      : 'bg-green/10 text-green hover:bg-green/20'
                    : isActive
                      ? 'bg-yellow-diff text-dark-bg'
                      : 'bg-dark-border-solid text-dark-faint hover:bg-dark-elevated'
                )}
                onClick={() => goToConflict(c)}
              >
                {c.resolved ? (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.467 3.72684C11.7559 3.91574 11.837 4.30308 11.6481 4.59198L7.39811 11.092C7.29795 11.2452 7.13568 11.3467 6.95414 11.3699C6.77259 11.3931 6.59001 11.3355 6.45458 11.2124L3.70458 8.71241C3.44917 8.48022 3.43035 8.08494 3.66254 7.82953C3.89473 7.57412 4.29001 7.5553 4.54542 7.78749L6.75304 9.79441L10.6019 3.90792C10.7908 3.61902 11.1781 3.53795 11.467 3.72684Z"
                      fill="currentColor"
                    />
                  </svg>
                ) : (
                  <>{i + 1}</>
                )}
              </button>
            )
          })}
          <IconButton
            onClick={next.goNext}
            disabled={!next.canNext}
            aria-label="Next"
          >
            <ArrowRightIcon />
          </IconButton>
        </div>
      </ScrollArea>
    </div>
  )
}
